import { PackageConfig } from '../models/app/PackageConfig';
import { PackageConfigEnvironments } from '../models/app/PackageConfigEnvironments.js';
import { getAppHydration, getManagerHydration } from '../utils/initialize';
import { getHydrationPoint } from '../utils/getHydrationPoint';
import { appendCacheQueryString } from '../utils/scripts';
import { TExternalScript } from '../models/commonTypes';
import { ApplicationParameters } from '../models/ApplicationParameters';
import { PackageJSON } from '../models/app/PackageJSON';

export class Manifest {
    static get packageJson(): PackageJSON {
        return require('../../package.json');
    }
    static get isDevelopment(): boolean {
        return process.env.NODE_ENV === 'development';
    }
    static get version(): string {
        return this.packageJson.version;
    }
    static get packageName(): string | undefined {
        return this.packageJson.name;
    }
    static get packageConfig(): PackageConfig {
        /* tslint:disable-next-line:no-string-literal */
        return (this.packageJson as any)['config'] || {};
    }
    static get proxy(): string | undefined {
        /* tslint:disable-next-line:no-string-literal */
        return (this.packageJson as any)['proxy'];
    }
    static get alias(): string | undefined {
        return this.packageConfig.alias;
    }
    static get description(): string | undefined {
        return this.packageConfig.description;
    }
    static get testEnvironments(): PackageConfigEnvironments {
        return this.packageConfig.testEnvironments;
    }
    static get bypassAppManager(): boolean {
        // tslint:disable-next-line: no-boolean-literal-compare
        return this.packageConfig.bypassAppManager === true;
    }
    static get ksysAppTemplateId(): string {
        return `${this.packageName}`;
    }
    static get storeKey(): string {
        return `${this.ksysAppTemplateId}-v${this.version}-store`;
    }
    static get externalScripts(): Array<TExternalScript> {
        const scripts: Array<TExternalScript> = [];
        if (!this.isDevelopment) {
            // TODO: add vendor files as peer dependencies
            // scripts.push(
            //     'vendor-mobx.4.9.4-react.16.8.6-react-dom.16.8.6-react-is.16.8.6-styled-components.5.2.1.min.js'
            // );
            const { peerDependencies } = this.packageJson;
            const templatePackageKey = '@kurtosys/ksys-app-template';
            const templateVersion = peerDependencies[templatePackageKey];
            if (templateVersion) {
                scripts.push(`ksys-app-template-v${templateVersion}.min.js`);
            }
        }

        // Polyfill URL API
        if (typeof window.URL !== 'function') {
            scripts.push(
                'https://unpkg.com/url-polyfill@1.1.5/url-polyfill.js'
            );
        }

        const managerAppResponse = getManagerHydration();
        const managerAssetCacheOptions =
            managerAppResponse &&
            managerAppResponse.applicationConfiguration &&
            managerAppResponse.applicationConfiguration.core &&
            managerAppResponse.applicationConfiguration.core.assetCacheOptions;
        if (managerAssetCacheOptions) {
            scripts.forEach((script, index) => {
                if (typeof script === 'string') {
                    scripts[index] = appendCacheQueryString(
                        script,
                        undefined,
                        managerAssetCacheOptions
                    );
                } else {
                    script.src = appendCacheQueryString(
                        script.src,
                        undefined,
                        managerAssetCacheOptions
                    );
                }
            });
        }

        return scripts;
    }
    /**
     * This guid is only used for local development testing (where do not have a script tag to pull an application code off of)
     *
     * @readonly
     */
    static get testGuid(): string | undefined {
        if (this.isDevelopment) {
            return this.packageConfig.testGuid;
        }
    }

    static get currentScript(): HTMLScriptElement | undefined {
        if (document.currentScript) {
            return document.currentScript as HTMLScriptElement;
        }
        const scripts = document.getElementsByTagName('script');
        const scriptNames = [
            `/main-${this.ksysAppTemplateId}.js`,
            `/main.js`, // OLD FC Fallback
        ];
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < scripts.length; i++) {
            const script = scripts[i];
            for (const scriptName of scriptNames) {
                if (script.src.indexOf(scriptName) > -1) {
                    return script;
                }
            }
        }
        return undefined;
    }

    static getBaseUrlPrefix(appParams?: ApplicationParameters) {
        let response: string = '';

        // define prefix based on hydration
        const hydrationPoint = getHydrationPoint();
        if (hydrationPoint && hydrationPoint.javascript && appParams) {
            const managerAppResponse = getManagerHydration();
            const appResponse = getAppHydration(appParams);
            if (appResponse && managerAppResponse) {
                const { app } = appResponse;
                const { applicationCode } = app;
                const { templateConfiguration } = managerAppResponse;
                const host =
                    templateConfiguration &&
                    templateConfiguration.externalScriptsBasePath;
                return `${host}/applicationManager/apps/${applicationCode}`;
            }
        }

        // else determine prefix based on script
        const scriptFilename = `/main-${this.packageName}.js`;
        const url = this.currentScript && this.currentScript.src;
        if (url) {
            const index = url.indexOf(scriptFilename);
            if (index > -1) {
                response = `${url.substr(0, index)}`;
            }
        }

        return response;
    }

    static getBaseUrl(
        suffix: string = 'services/',
        appParams?: ApplicationParameters
    ) {
        return `${this.getBaseUrlPrefix(appParams)}/${suffix}`;
    }

    /**
     * Get App GUID Based on proxy | app manager hydration | script
     *
     * @static
     * @param [appParams]
     * @returns
     */
    static getApplicationGuid(
        appParams?: ApplicationParameters
    ): string | undefined {
        if (this._applicationGuid) {
            return this._applicationGuid;
        }
        const applicationUrlPath = this.isDevelopment
            ? this.proxy
            : this.getBaseUrlPrefix(appParams);
        if (applicationUrlPath) {
            const urlParts = applicationUrlPath.split('/');
            if (urlParts.length > 0) {
                this._applicationGuid = urlParts[urlParts.length - 1];
                return this._applicationGuid;
            }
        }
    }

    static serialize(): object {
        const keys: Array<string> = [
            'alias',
            'bypassAppManager',
            'description',
            'externalScripts',
            'getBaseUrl',
            'ksysAppTemplateId',
            'packageConfig',
            'packageName',
            'storeKey',
            'version',
        ];
        const response: object = {};
        keys.forEach(key => {
            response[key] =
                Manifest[key] instanceof Function
                    ? Manifest[key]()
                    : Manifest[key];
        });

        return response;
    }

    // tslint:disable-next-line: variable-name
    private static _applicationGuid: string;
}



// WEBPACK FOOTER //
// ./src/configuration/Manifest.ts